import type { MeetingAnalysis, SourceReference, TopicStatus, VisualizationKind } from '@shared/contract';
import { formatDuration } from '../lib/format';

const STATUS: Record<TopicStatus, string> = {
  decided: 'Beslut',
  unresolved: 'Inte klart',
  unowned: 'Ingen tog ansvar',
  discussed: 'Diskuterades',
  side_track: 'Sidospår',
};

/** Hur bildens former ska läsas. Texten står här i stället för i bilden, där bildmodellen ofta stavar fel. */
const LEGEND: Record<'city' | 'rocket', Partial<Record<TopicStatus | 'height' | 'intense' | 'order' | 'actions', string>>> = {
  city: {
    height: 'Höjden visar hur länge ämnet diskuterades',
    decided: 'Monument: ett beslut fattades',
    unresolved: 'Skelett med kran: inte klart',
    unowned: 'Tom tomt: ingen tog ansvar',
    intense: 'Bilkö: intensiv diskussion',
    side_track: 'Hus vid kanten: sidospår',
    actions: 'Väg ut från ön: att göra',
  },
  rocket: {
    order: 'Nerifrån och upp: i den ordning ämnena kom',
    height: 'Höjden visar hur länge ämnet diskuterades',
    decided: 'Guldmärke: ett beslut fattades',
    unresolved: 'Ställning: inte klart',
    unowned: 'Hål: ingen tog ansvar, och då lyfter den inte',
    intense: 'Folksamling: intensiv diskussion',
    side_track: 'Tältet: sidospår',
    actions: 'Person på tornet: att göra',
  },
};

function Jump({ refItem, ms, onJump }: { refItem?: SourceReference; ms: number; onJump: (ref: SourceReference) => void }) {
  return refItem ? (
    <button className="ref" title={refItem.quote} onClick={() => onJump(refItem)}>
      {formatDuration(ms)}
    </button>
  ) : null;
}

export function ImageKey({ kind, analysis, onJump }: { kind: VisualizationKind; analysis: MeetingAnalysis; onJump: (ref: SourceReference) => void }) {
  if (kind === 'comic') {
    const shown = analysis.moments.length >= 6 ? 6 : analysis.moments.length >= 4 ? 4 : analysis.moments.length;
    return (
      <ol className="image-key">
        {analysis.moments.slice(0, shown).map((m, i) => (
          <li key={i}>
            <Jump refItem={m.references[0]} ms={m.start_ms} onJump={onJump} /> {m.caption}
          </li>
        ))}
      </ol>
    );
  }
  const { topics, actions } = analysis;
  const legend = kind === 'city' || kind === 'rocket' ? LEGEND[kind] : null;
  const present = new Set<string>(['height', 'order', ...topics.map((t) => t.status)]);
  if (topics.some((t) => t.intense)) present.add('intense');
  if (actions.length) present.add('actions');
  return (
    <div className="image-key-grid">
      <div>
        <h4>Ämnen</h4>
        <ul className="image-key">
          {topics.map((t) => (
            <li key={t.name}>
              <Jump refItem={t.references[0]} ms={t.start_ms} onJump={onJump} /> <strong>{t.name}</strong>{' '}
              <span className="muted">
                {STATUS[t.status]}
                {t.decision ? `: ${t.decision}` : ''}
                {t.intense ? ', intensivt' : ''}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {legend && (
        <div>
          <h4>Så läser du bilden</h4>
          <ul className="image-key">
            {Object.entries(legend)
              .filter(([key]) => present.has(key))
              .map(([key, text]) => (
                <li key={key}>{text}</li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
